import { useForm } from "@inertiajs/react";
import {
  Children,
  cloneElement,
  type FormEvent,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

import Button from "@/components/Button";
import LinkButton from "@/components/LinkButton";
import SearchableSelect from "@/components/SearchableSelect";
import { centers } from "@/routes";
import type { Center, CenterStatus } from "@/types";

const STATUS_LABELS: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
};

const FIELD_INPUT_CLASSES =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink shadow-none placeholder:text-ink/40 focus:outline-none focus:ring-0 focus:shadow-none";

function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-4 rounded-2xl border-2 border-ink/20 p-5">
      <legend className="flex items-center gap-2 px-2 font-sans text-sm font-semibold text-ink">
        <span className="text-steel">{icon}</span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

/** Labels a single input/select the same way `Section` labels a card: the
 * name sits on the border as a legend once the field has a value, so a
 * filled field stays identifiable. While empty, only the placeholder (also
 * derived from `label`) shows, so the two don't display redundantly. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  const child = Children.only(children);
  const childProps = isValidElement(child)
    ? (child.props as { value?: unknown; required?: boolean })
    : undefined;
  const hasValue =
    childProps?.value !== undefined &&
    childProps?.value !== null &&
    childProps?.value !== "";
  const invalid = !!childProps?.required && !hasValue;
  const displayLabel = childProps?.required ? `${label} *` : label;
  const showLegend = hasValue || invalid;

  const content = isValidElement(child)
    ? cloneElement(child as ReactElement<{ placeholder?: string }>, {
        placeholder:
          (child.props as { placeholder?: string }).placeholder ?? displayLabel,
      })
    : child;

  return (
    <fieldset
      className={`min-w-0 rounded-full border-2 px-4 focus-within:border-steel ${showLegend ? "pb-2.5" : "py-3.5"} ${invalid ? "border-deep-red" : "border-ink"}`}
    >
      {showLegend && (
        <legend
          className={`truncate px-1.5 font-sans text-xs ${invalid ? "text-deep-red" : "text-ink/60"}`}
        >
          {displayLabel}
        </legend>
      )}
      {content}
    </fieldset>
  );
}

function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={FIELD_INPUT_CLASSES} {...props} />;
}

type Props = {
  data: Center;
};

export default function CentersEdit({ data: center }: Props) {
  const form = useForm({
    name: center.name ?? "",
    status: center.status ?? "",
    director_name: center.director_name ?? "",
    director_email: center.director_email ?? "",
    mandated_name: center.mandated_name ?? "",
    mandated_email: center.mandated_email ?? "",
  });

  const isNew = !center.id;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      e.currentTarget.reportValidity();
      return;
    }
    form.transform((data) => ({ center: data }));
    if (isNew) {
      form.post(centers.create().url);
    } else {
      form.patch(centers.update(center.id).url);
    }
  }

  return (
    <div className="mx-auto w-[90%] max-w-2xl py-10">
      <h1 className="mb-1 font-sans text-3xl font-semibold text-ink">
        {isNew ? "Nuevo centro" : center.name || "Centro"}
      </h1>
      <p className="mb-6 font-sans text-sm text-ink/60">
        Los campos marcados con * son obligatorios.
      </p>

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Section icon={<BuildingIcon />} title="Datos generales">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre">
              <Input
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                required
              />
            </Field>
            <Field label="Estado">
              <SearchableSelect
                value={form.data.status}
                onChange={(v) => form.setData("status", v as CenterStatus)}
                options={Object.entries(STATUS_LABELS).map(
                  ([value, label]) => ({ value, label }),
                )}
              />
            </Field>
          </div>
        </Section>

        <Section icon={<UsersIcon />} title="Responsables">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre director/a">
              <Input
                value={form.data.director_name}
                onChange={(e) => form.setData("director_name", e.target.value)}
              />
            </Field>
            <Field label="Email director/a">
              <Input
                type="email"
                value={form.data.director_email}
                onChange={(e) => form.setData("director_email", e.target.value)}
              />
            </Field>
            <Field label="Nombre encargado/a plataforma">
              <Input
                value={form.data.mandated_name}
                onChange={(e) => form.setData("mandated_name", e.target.value)}
              />
            </Field>
            <Field label="Email encargado/a plataforma">
              <Input
                type="email"
                value={form.data.mandated_email}
                onChange={(e) => form.setData("mandated_email", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <div className="mt-2 flex gap-3">
          <Button type="submit" disabled={form.processing}>
            Guardar
          </Button>
          <LinkButton href={centers.index().url}>Cancelar</LinkButton>
        </div>
      </form>
    </div>
  );
}

function BuildingIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="4" y="3" width="12" height="18" rx="1" />
      <path strokeLinecap="round" d="M8 7h4M8 11h4M8 15h4M16 10h4v11h-4" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="9" cy="8" r="3" />
      <path strokeLinecap="round" d="M3 19c0-3 2.5-5 6-5s6 2 6 5" />
      <path strokeLinecap="round" d="M16 4.5a3 3 0 0 1 0 6" />
      <path strokeLinecap="round" d="M17 14c2.5.3 4 1.8 4 4.5" />
    </svg>
  );
}
